import { Autocomplete, Chip, TextField } from "@mui/material";
import Component from "components/Component";
import Disabled from "components/common/Disabled";
import * as labels from "config.labels";
import { SIC_CODE_INDUSTRY_SUGGESTION } from "flags";
import { useMemo, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import * as staticDataService from "services/staticDataService";
import * as programActions from "store/actions/input/program/programActions";
import { useFeatureFlag } from "store/hooks/featureFlagHooks";
import * as staticDataHooks from "store/hooks/staticDataHooks";

const SIC_SEARCH_CONFIG = {
  MAX_RESULTS: 100,
};

const titleCase = (str) =>
  str.toLowerCase().replace(/(?:^|\s|[-/])\S/g, (match) => match.toUpperCase());

const formatDisplayTitle = (title) => {
  if (!title) return "";
  const dashIndex = title.indexOf("-");
  if (dashIndex === -1) return titleCase(title);
  const prefix = title.slice(0, dashIndex).trim();
  const suffix = title.slice(dashIndex + 1).trim();
  return `${titleCase(suffix)} (${titleCase(prefix)})`;
};

const getOptionLabel = (option) => {
  if (!option || typeof option !== "object") return "";
  return `(${option.code}) ${formatDisplayTitle(option.title)}`;
};

const SICCodeSearch = (props) => {
  const translateLabel = staticDataHooks.useTranslator("uiLabels");
  const dispatch = useDispatch();
  const companySic = useSelector((state) => state.input.program.companySic);
  const program = useSelector((state) => state.input.program);
  const { isOn: showSuggestion } = useFeatureFlag(SIC_CODE_INDUSTRY_SUGGESTION);
  const [inputValue, setInputValue] = useState(
    companySic ? getOptionLabel(companySic) : ""
  );

  const sicCodesQuery = staticDataService.useConfigQuery("sic_codes");
  const classCodeMappingsQuery = staticDataService.useConfigQuery(
    "codes_to_sic_mappings"
  );

  const sicCodes = useMemo(() => {
    const codes = sicCodesQuery.data?.codes;
    if (!Array.isArray(codes)) return [];
    return codes;
  }, [sicCodesQuery.data]);

  const filteredOptions = useMemo(() => {
    const query = inputValue.toLowerCase().trim();
    if (!query) return sicCodes;
    return sicCodes
      .filter(
        (item) =>
          String(item.code).includes(query) ||
          item.title.toLowerCase().includes(query) ||
          formatDisplayTitle(item.title).toLowerCase().includes(query)
      )
      .slice(0, SIC_SEARCH_CONFIG.MAX_RESULTS);
  }, [inputValue, sicCodes]);

  const handleChange = (_, newValue) => {
    if (newValue && typeof newValue === "object") {
      dispatch(
        programActions.updateProgram({
          companySic: { code: newValue.code, title: newValue.title },
        })
      );
    } else {
      dispatch(programActions.updateProgram({ companySic: null }));
    }
  };

  const sicConfig = classCodeMappingsQuery.data?.sic ?? null;

  const suggestedSicCode = useMemo(() => {
    if (!showSuggestion || companySic || !sicConfig) return null;
    const lookupValue = program[sicConfig.submissionKey];
    if (!lookupValue) return null;
    const code = (sicConfig.mapping ?? {})[lookupValue];
    if (!code) return null;
    return sicCodes.find((s) => String(s.code) === String(code)) ?? null;
  }, [showSuggestion, companySic, program, sicConfig, sicCodes]);

  const acceptSuggestion = () => {
    if (!suggestedSicCode) return;
    dispatch(
      programActions.updateProgram({
        companySic: {
          code: suggestedSicCode.code,
          title: suggestedSicCode.title,
        },
      })
    );
    setInputValue(getOptionLabel(suggestedSicCode));
  };

  const label = translateLabel(labels.COMPANY_SIC, { default: "SIC Code" });

  return (
    <Component
      title={label}
      subtitle={translateLabel(labels.COMPANY_SIC_SUBTITLE, {
        default: "Standard Industrial Classification",
      })}
      {...props.componentProps}
    >
      {suggestedSicCode && (
        <Chip
          label={`Suggested based on ${
            sicConfig?.name ?? sicConfig?.submissionKey
          }: ${getOptionLabel(suggestedSicCode)}`}
          size={"small"}
          variant={"outlined"}
          onClick={acceptSuggestion}
          sx={{
            mt: -2,
            mb: 0.5,
            cursor: "pointer",
            color: "primary.main",
            borderColor: "primary.main",
          }}
        />
      )}
      <Disabled ifReadOnly>
        <Autocomplete
          options={filteredOptions}
          getOptionLabel={getOptionLabel}
          isOptionEqualToValue={(option, value) => option.code === value.code}
          value={companySic ?? null}
          inputValue={inputValue}
          onInputChange={(_, newInputValue, reason) => {
            if (reason === "input") {
              setInputValue(newInputValue);
            } else if (reason === "clear") {
              setInputValue("");
            } else if (reason === "reset" && companySic) {
              setInputValue(getOptionLabel(companySic));
            }
          }}
          onChange={handleChange}
          filterOptions={(x) => x}
          fullWidth
          renderInput={(params) => (
            <TextField
              {...params}
              label={label}
              variant={"filled"}
              InputLabelProps={{ shrink: true }}
              fullWidth
            />
          )}
          renderOption={(props, option) => {
            const query = inputValue.toLowerCase().trim();
            const displayLabel = getOptionLabel(option);
            const parts = query
              ? displayLabel.split(
                  new RegExp(
                    `(${query.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")})`,
                    "gi"
                  )
                )
              : [displayLabel];

            return (
              <li {...props} key={option.code}>
                <span>
                  {parts.map((part, index) =>
                    part.toLowerCase() === query ? (
                      <span
                        key={index}
                        style={{
                          fontWeight: "bold",
                          backgroundColor: "#e3f2fd",
                        }}
                      >
                        {part}
                      </span>
                    ) : (
                      part
                    )
                  )}
                </span>
              </li>
            );
          }}
        />
      </Disabled>
    </Component>
  );
};

export default SICCodeSearch;
